// Font registry (M14): loads the bundled OFL fonts (public/fonts, precached) with opentype.js (MIT) once, and
// hands out parsed fonts by family + weight. Text is drawn from the glyph outlines, so the canvas, the PNG and
// the PDF use exactly the same shapes and no font is embedded anywhere.
import opentype from 'opentype.js';
import { FONT_FAMILIES, fontFile, fontKey, nearestWeight } from './fontCatalog';
import { gposKerning, type KernFn } from './kerning';

const fonts = new Map<string, opentype.Font>();
const kerns = new Map<string, KernFn>();
let loading: Promise<void> | null = null;
/** Bumped when fonts finish loading (layout caches compare it). */
export let fontEpoch = 0;

const base = (): string => (import.meta.env?.BASE_URL as string | undefined) ?? '/';

export function fontsReady(): boolean {
  return fontEpoch > 0;
}

/** Loads every bundled font (about 0.6 MB). Safe to call many times. */
export function loadFonts(): Promise<void> {
  loading ??= (async () => {
    const jobs: Promise<void>[] = [];
    for (const f of FONT_FAMILIES)
      for (const w of f.weights)
        jobs.push(
          (async () => {
            const res = await fetch(base() + fontFile(f, w));
            if (!res.ok) throw new Error(`font ${fontFile(f, w)}: HTTP ${res.status}`);
            const buf = await res.arrayBuffer();
            registerFont(f.family, w, opentype.parse(buf), new Uint8Array(buf), false);
          })(),
        );
    await Promise.all(jobs);
    fontEpoch++;
  })();
  loading.catch(() => (loading = null));
  return loading;
}

/** The parsed font for a family/weight (nearest weight the family has), or null before loadFonts(). */
export function getFont(family: string, weight: number): opentype.Font | null {
  const f = nearestWeight(family, weight);
  return fonts.get(fontKey(f.family, f.weight)) ?? null;
}

/** Pair kerning for a family/weight: GPOS 'kern' (Extension lookups included), else the 'kern' table. */
export function getKern(family: string, weight: number): KernFn {
  const f = nearestWeight(family, weight);
  const k = kerns.get(fontKey(f.family, f.weight));
  if (k) return k;
  const font = fonts.get(fontKey(f.family, f.weight));
  return font ? (l, r) => font.getKerningValue(l, r) : () => 0;
}

/** Registers a parsed font (also used by tests in Node, which read the files without fetch). */
export function registerFont(
  family: string,
  weight: number,
  font: opentype.Font,
  bytes?: Uint8Array,
  bump = true,
) {
  const key = fontKey(family, weight);
  fonts.set(key, font);
  const k = bytes ? gposKerning(bytes) : null;
  if (k) kerns.set(key, k);
  else kerns.delete(key);
  if (bump) fontEpoch = Math.max(fontEpoch, 1);
}
